import { useRef, useState } from "react";
import Popup from "../components/Popup";
import UploadImage from "../components/UploadImage";
import ViewCarousel from "../components/ViewCarousel";
import { Toaster, toast } from "react-hot-toast";
import { getLevelFromUrl } from "../components/getLevelFromUrl";
import axios from "axios";
import { Upload, Eye } from "lucide-react";

const ERLandingPage = ({ navigation }) => {
  const fileInputRef = useRef(null);
  const [diagrams, setDiagrams] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [viewDiagrams, setViewDiagrams] = useState([]);
  const [showView, setShowView] = useState(false);
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loadingView, setLoadingView] = useState(false);
  const [viewError, setViewError] = useState(false);

  const defaultLevel = navigation?.level;
  const defaultValue = navigation?.selectedL1;

  const userEmail = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");

  const canSubmit =
    diagrams.length > 0 && diagrams.every((d) => d.description.trim());

  const handleFileSelect = (files) => {
    Array.from(files).forEach((file, idx) => {
      const reader = new FileReader();

      reader.onload = () => {
        setDiagrams((prev) => [
          ...prev,
          {
            id: Date.now() + idx,
            name: file.name,
            size: file.size,
            file,
            base64: reader.result,
            preview: URL.createObjectURL(file),
            description: "",
          },
        ]);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleRemove = (id) => {
    setDiagrams((prev) => prev.filter((d) => d.id !== id));
  };

  const handleDescriptionChange = (id, value) => {
    setDiagrams((prev) =>
      prev.map((d) => (d.id === id ? { ...d, description: value } : d)),
    );
  };

  const base64ToFile = async (base64, filename) => {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  };
  
  const handleSubmit = async () => {
    const { level, value } = getLevelFromUrl();
  
    const formData = new FormData();
  
    formData.append("user_id", userEmail);
    formData.append("level", level ? level : defaultLevel?.toLowerCase());
    formData.append("value", value ? value : defaultValue);
  
    for (const diagram of diagrams) {
      let fileToSend = diagram.file;
  
      if (!fileToSend && diagram.base64) {
        fileToSend = await base64ToFile(diagram.base64, diagram.name);
      }
  
      if (fileToSend) {
        formData.append("files", fileToSend);
        formData.append("descriptions", diagram.description || "");
      }
    }
  
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/upload-erDiagrams`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
  
      toast.success("ER Diagrams uploaded successfully");
      setShowUpload(false);
    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
      setShowUpload(false);
    }
  };
  

  const openViewModal = async () => {
    const { level, value } = getLevelFromUrl();

    setLoadingView(true);
    setViewError(false);
    setShowView(true);
    setIndex(0);
    setZoom(1);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-erDiagrams?level=${level ? level : defaultLevel?.toLowerCase()}&value=${value ? encodeURIComponent(value) : defaultValue}`,
      );

      const formatted = res.data.erDiagrams.map((d) => ({
        name: d.fileName,
        description: d.description,
        preview: d.imageData,
      }));

      setViewDiagrams(formatted);
    } catch (err) {
      console.error(err);
      setViewError(true);
      toast.error("Unable to load ER diagrams. Showing sample data.");
    } finally {
      setLoadingView(false);
    }
  };

  const openUploadModal = async () => {
    const { level, value } = getLevelFromUrl();

    setShowUpload(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-erDiagrams?level=${level ? level : defaultLevel?.toLowerCase()}&value=${value ? encodeURIComponent(value) : defaultValue}`,
      );

      const existing = res.data.erDiagrams.map((d, idx) => ({
        id: `existing-${idx}`,
        name: d.fileName,
        size: d.size || 0,
        base64: d.imageData,
        preview: d.imageData, // already base64
        description: d.description || "",
        isExisting: true,
      }));

      setDiagrams(existing);
    } catch (err) {
      console.warn("No existing diagrams found");
      setDiagrams([]);
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontWeight: 500 },
        }}
      />

      <div className="flex gap-2">
        {userRole === "Admin" && (
          <button
            onClick={openUploadModal}
            className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
          >
            <Upload className="h-5" />
            Upload Design Diagrams
          </button>
        )}

        <button
          onClick={openViewModal}
          className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
        >
          <Eye className="h-5" />
          View Design Diagrams
        </button>
      </div>

      {showUpload && (
        <Popup
          title="Upload Design Diagrams"
          onClose={() => setShowUpload(false)}
          maxWidth="1100px"
          height="85vh"
        >
          <UploadImage
            diagrams={diagrams}
            fileInputRef={fileInputRef}
            onFileSelect={handleFileSelect}
            onRemove={handleRemove}
            onDescriptionChange={handleDescriptionChange}
            onSubmit={handleSubmit}
            canSubmit={canSubmit}
          />
        </Popup>
      )}

      {showView && (
        <Popup
          title="View Design Diagrams"
          onClose={() => setShowView(false)}
          maxWidth="1600px"
          height="92vh"
          minHeight="90vh"
        >
          {loadingView ? (
            <div className="h-[420px] flex items-center justify-center text-gray-500">
              Loading Design Diagrams...
            </div>
          ) : (
            <ViewCarousel
              diagrams={viewDiagrams}
              index={index}
              setIndex={setIndex}
              zoom={zoom}
              setZoom={setZoom}
              resetZoom={() => setZoom(1)}
            />
          )}
        </Popup>
      )}
    </>
  );
};

export default ERLandingPage;
